import { useState } from "react";
import { DRAMA_TYPES, REGIONS, YEARS } from "@/utils/content";

export interface FilterValue {
  type: string;
  region: string;
  year: string;
  sort: string;
}

interface FilterBarProps {
  value: FilterValue;
  onChange: (next: Partial<FilterValue>) => void;
  onReset: () => void;
}

const SORTS = [
  { key: "new", label: "最新上线" },
  { key: "hot", label: "最热" },
  { key: "rating", label: "高分" },
];

function Chip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 px-3.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-background-200 text-foreground-700 hover:bg-background-300"
      }`}
    >
      {label}
    </button>
  );
}

function FilterRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 py-3.5 border-b border-background-200 last:border-b-0 sm:flex-row sm:items-center sm:gap-4">
      <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterBar({ value, onChange, onReset }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeCount = [value.type, value.region, value.year].filter(Boolean).length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setMobileOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3.5 md:hidden"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line text-accent-600"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-2 py-0.5 text-xs text-background-50">{activeCount}</span>
          )}
        </span>
        <i className={`text-lg text-foreground-500 ${mobileOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
      </button>

      <div className={`${mobileOpen ? "block" : "hidden"} md:block px-4 py-2 md:py-3`}>
        <FilterRow label="类型">
          <Chip active={!value.type} label="全部" onClick={() => onChange({ type: "" })} />
          {DRAMA_TYPES.map((type) => (
            <Chip key={type} active={value.type === type} label={type} onClick={() => onChange({ type })} />
          ))}
        </FilterRow>
        <FilterRow label="地区">
          <Chip active={!value.region} label="全部" onClick={() => onChange({ region: "" })} />
          {REGIONS.map((region) => (
            <Chip key={region} active={value.region === region} label={region} onClick={() => onChange({ region })} />
          ))}
        </FilterRow>
        <FilterRow label="年份">
          <Chip active={!value.year} label="全部" onClick={() => onChange({ year: "" })} />
          {YEARS.map((year) => (
            <Chip
              key={year}
              active={value.year === String(year)}
              label={`${year}`}
              onClick={() => onChange({ year: String(year) })}
            />
          ))}
        </FilterRow>
        <FilterRow label="排序">
          {SORTS.map((sort) => (
            <Chip
              key={sort.key}
              active={value.sort === sort.key}
              label={sort.label}
              onClick={() => onChange({ sort: sort.key })}
            />
          ))}
          <button
            type="button"
            onClick={onReset}
            className="ml-auto inline-flex items-center gap-1 h-8 px-3 rounded-full text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置
          </button>
        </FilterRow>
      </div>
    </div>
  );
}